package gr.tech.site.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.icons.fa.FaCalendar
import com.varabyte.kobweb.silk.components.icons.fa.FaUser
import com.varabyte.kobweb.silk.components.icons.fa.IconStyle
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobwebx.markdown.markdown
import gr.tech.site.styles.Theme
import gr.tech.site.styles.wideCappedWidth
import gr.tech.site.toSitePalette
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

val MarkdownStyle = CssStyle {
    base {
        Modifier
            .margin(top = 1.cssRem, bottom = 3.cssRem)
            .wideCappedWidth()
    }

    cssRule("img") {
        Modifier
            .display(DisplayStyle.Block)
            .margin(topBottom = 2.5.cssRem, leftRight = autoLength)
            .maxWidth(100.percent)
    }

    cssRule("h1") {
        Modifier
            .fontSize(3.cssRem)
            .fontWeight(400)
            .margin(bottom = 2.5.cssRem)
            .lineHeight(1.2) //1.5x doesn't look as good on very large text
    }

    cssRule("h2") {
        Modifier
            .fontSize(3.cssRem)
            .fontWeight(300)
            .margin(topBottom = 2.cssRem)
    }

    cssRule("h3") {
        Modifier
            .fontSize(2.4.cssRem)
            .fontWeight(300)
            .margin(topBottom = 1.5.cssRem)
    }

    cssRule("h4") {
        Modifier
            .fontSize(1.2.cssRem)
            .fontWeight(FontWeight.Bolder)
            .margin(top = 1.cssRem, bottom = 0.5.cssRem)
    }

    cssRule("ul") {
        Modifier.fillMaxWidth().overflowWrap(OverflowWrap.BreakWord)
    }

    cssRule(" :is(li,ol,ul)") {
        Modifier.margin(bottom = 0.25.cssRem)
    }

    cssRule("code") {
        Modifier
            .color(colorMode.toPalette().color.toRgb().copyf(alpha = 0.8f))
            .fontWeight(FontWeight.Bolder)
    }

    cssRule("pre") {
        Modifier
            .margin(top = 0.5.cssRem, bottom = 2.cssRem)
            .fillMaxWidth()
    }
    cssRule("pre > code") {
        Modifier
            .display(DisplayStyle.Block)
            .fillMaxWidth()
            .backgroundColor(colorMode.toSitePalette().nearBackground)
            .border(1.px, LineStyle.Solid, colorMode.toPalette().color)
            .borderRadius(0.25.cssRem)
            .padding(0.5.cssRem)
            .fontSize(1.cssRem)
            .overflow { x(Overflow.Auto) }
    }
}

@InitRoute
fun initMarkdownLayout(ctx: InitRouteContext) {
    val title = ctx.markdown!!.frontMatter["title"]?.singleOrNull()
    require(title != null) { "Markdown file must set \"title\" in frontmatter" }

    val description = ctx.markdown!!.frontMatter["description"]?.singleOrNull()

    val routeOverride = ctx.markdown!!.frontMatter["routeOverride"]?.singleOrNull()?.trim()
        ?: title.lowercase().replace(Regex("[^a-z0-9]+"), "-").trim('-')

    val canonicalUrl = "https://greenrebates.com/blog/$routeOverride"
    val ogImage = ctx.markdown!!.frontMatter["image"]?.singleOrNull()?.trim()

    ctx.data.add(
        PageLayoutData(
            showContactHeader = true,
            title = title,
            description = description,
            canonicalUrl = canonicalUrl,
            ogImage = ogImage,
            ogType = "article"
        )
    )
}

@Composable
@Layout(".components.layouts.PageLayout")
fun MarkdownLayout(
    content: @Composable () -> Unit,
) {
    val ctx = rememberPageContext()
    val title = ctx.markdown!!.frontMatter["title"]?.singleOrNull()
    val author = ctx.markdown!!.frontMatter["author"]?.singleOrNull()
    val date = ctx.markdown!!.frontMatter["date"]?.singleOrNull()
    val imageUrl = ctx.markdown!!.frontMatter["image"]?.singleOrNull()

    LaunchedEffect(title) {
        fun setOrCreateMeta(property: String, content: String) {
            val el = document.querySelector("meta[property='$property']")
                ?: document.createElement("meta").also {
                    it.setAttribute("property", property)
                    document.head!!.appendChild(it)
                }
            el.setAttribute("content", content)
        }
        author?.let { setOrCreateMeta("article:author", it) }
        date?.let { setOrCreateMeta("article:published_time", it) }
    }

    Div(MarkdownStyle.toModifier().classNames("px-1", "mx-auto").toAttrs()) {
        Div(
            Modifier
                .classNames("d-flex", "flex-wrap", "gap-4", "align-items-center", "mt-5", "mb-2")
                .padding(bottom = 1.5.cssRem)
                .borderBottom(1.px, LineStyle.Solid, Theme.MediumGreen.rgb,)
                .toAttrs {}
        ) {
            // Author section
            author?.let {
                Div(
                    Modifier
                        .classNames("d-flex", "align-items-center", "gap-2")
                        .toAttrs()
                ) {
                    Span(Modifier.classNames("fs-6").toAttrs()) {
                        FaUser(style = IconStyle.FILLED)
                    }
                    P(
                        Modifier
                            .classNames("fs-6", "fw-medium", "mb-0")
                            .color(Theme.DarkGreen.rgb)
                            .toAttrs()
                    ) {
                        Text(author)
                    }
                }
            }

            // Date section
            date?.let {
                Div(
                    Modifier
                        .classNames("d-flex", "align-items-center", "gap-2")
                        .toAttrs()
                ) {
                    Span(Modifier.classNames("fs-6").toAttrs()) {
                        FaCalendar()
                    }
                    P(
                        Modifier
                            .classNames("fs-6", "mb-0")
                            .color(Theme.DarkGreen.rgb)
                            .opacity(0.8)
                            .toAttrs()
                    ) {
                        Text(date)
                    }
                }
            }
        }

        imageUrl?.let { url ->
            Div(attrs = Modifier.classNames("text-center", "my-4").toAttrs()) {
                Img(
                    src = url,
                    alt = "Thumbnail for ${title ?: "Blog"}",
                    attrs = Modifier.classNames("img-fluid", "rounded", "w-75")
                        .maxHeight(480.px)
                        .objectFit(ObjectFit.Contain)
                        .toAttrs()
                )
            }
        }

        // Actual Markdown Content
        content()
    }
}
